import { LoadingState } from '@lcs/shared/api';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';

import { RegisterState } from 'src/app/common-components/register/models/register-state';
import {
   RegisterAdapter,
   createRegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';

import { accountIdChange } from 'src/app/store/select-account/select-account.actions';

import { AllowedIpFilterOptions } from '../models/allowed-ip-filter-option';
import { AllowedIpRegisterOptions } from '../models/allowed-ip-register-options';
import { AllowedIpRegisterListItem } from '../models/allowed-ip-register-results.model';

import * as allowedIpRegisterActions from './allowed-ip-register.actions';

export const allowedIpRegisterFeatureKey = 'allowedIpRegister';

export interface AllowedIpRegisterState
   extends RegisterState<AllowedIpRegisterListItem, AllowedIpRegisterOptions, OrderingOption> {
   filters: AllowedIpFilterOptions[];
   orderingOptions: OrderingOption[];
}

const adapter: RegisterAdapter<AllowedIpRegisterListItem, AllowedIpRegisterOptions, OrderingOption> =
   createRegisterAdapter<AllowedIpRegisterListItem, AllowedIpRegisterOptions, OrderingOption>({
      selectId: (item) => item.allowedIpAddressId ?? 0,
   });

const initialState: AllowedIpRegisterState = {
   ...adapter.getInitialState(),
   filters: [],
   orderingOptions: [
      {
         field: 'ipAddress',
         direction: OrderingDirections.Asc,
      },
   ],
};

export const allowedIpRegisterReducer = adapter.createReducer(
   allowedIpRegisterActions.allowedIpRegisterAdapterActions,
   initialState,

   on(allowedIpRegisterActions.allowedIpRegisterAdapterActions.pageEnter, (state) => ({
      ...state,
      callState: LoadingState.Loading,
   })),

   on(allowedIpRegisterActions.allowedIpRegisterAdapterActions.loadSuccess, (state, action) => ({
      ...state,
      callState: LoadingState.Loaded,
      endCursor: action.endCursor,
      hasNextPage: action.hasNextPage,
      items: action.items,
      totalCount: action.totalCount,
   })),

   on(allowedIpRegisterActions.allowedIpRegisterAdapterActions.quickFilterChanged, (state, action) => ({
      ...state,
      callState: LoadingState.Loading,
      quickFilters: {
         ...state.quickFilters,
         ...action.quickFilters,
      },
   })),
   on(allowedIpRegisterActions.editAllowedIpSubmit, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(allowedIpRegisterActions.editAllowedIpSuccess, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
      };
   }),
   on(allowedIpRegisterActions.editAllowedIpFailure, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
      };
   }),
   on(allowedIpRegisterActions.allowedIpRegisterAdapterActions.orderingOptionsChanged, (state, action) => ({
      ...state,
      callState: LoadingState.Loading,
      orderingOptions: state.orderingOptions
         ? [
              ...state.orderingOptions
                 .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                 .filter((option) => option.direction !== OrderingDirections.NotSet),
              ...action.orderingOptions,
           ]
         : action.orderingOptions,
   })),

   on(accountIdChange, (state) => ({
      ...state,
      quickFilters: initialState.quickFilters,
   }))
);

export const allowedIpRegisterSelector = createFeatureSelector<AllowedIpRegisterState>(allowedIpRegisterFeatureKey);

export const selectors = adapter.getSelectors(allowedIpRegisterSelector);

export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectEndCursor,
   selectQuickFilters,
   selectOrderingOptions,
} = selectors;
